/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState } from 'react';
import { Modal, Upload } from 'antd';
import { useIntl } from 'react-intl';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './UploadFileModal.styles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import IconButton from '~components/_common/IconButton/IconButton';
import { InboxOutlined, PaperClipOutlined } from '@ant-design/icons';
import { appLog } from '~src/utils/log';

export const UploadFileModal = ({
  maxFiles,
  onCancel,
  onConfirm,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const { Dragger } = Upload;
  const [fileList, setFileList] = useState<any[]>([]);

  const isValid = () => fileList.length > 0;

  const handleSubmit = async () => {
    if (!isValid()) return;
    appLog(`TUS: filelist pushed:`, fileList);
    onConfirm?.(fileList);
    setFileList([]);
  };

  const handleUploadChange = (info: any) => {
    appLog('TUS: Upload info:', info);
    setFileList(info.fileList);
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'upload-file' })}
          icon={['far', 'ticket']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ disabled: !isValid() }}
      onOk={handleSubmit}
      onCancel={() => {
        setFileList([]);
        onCancel?.();
      }}
      destroyOnClose
      {...modalProps}>
      <div css={styles.groupCardFileWrapper}>
        <Dragger
          multiple={maxFiles > 1}
          maxCount={maxFiles}
          showUploadList
          listType="text"
          fileList={fileList}
          onChange={handleUploadChange}
          itemRender={(originNode, file, fileList, actions) => {
            const isPdf =
              file.type === 'application/pdf' || file.name.endsWith('.pdf');
            const maxNameLength = 30;
            const isTooLong = file.name.length > maxNameLength;
            const displayName = isTooLong
              ? file.name.slice(0, maxNameLength) + '…'
              : file.name;

            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                {isPdf ? (
                  <IconButton
                    icon={'file-pdf'}
                    iconStyle={'fad'}
                    color={'red'}
                    backgroundOff
                    customStyle={{
                      fontSize: '18px',
                    }}
                    containerStyle={{
                      maxHeight: '18px',
                      maxWidth: '18px',
                      minHeight: '18px',
                      minWidth: '18px',
                    }}
                  />
                ) : (
                  <PaperClipOutlined style={{ fontSize: '18px' }} />
                )}
                <span>{displayName}</span>
                <IconButton
                  click={() => actions.remove(file)}
                  icon={'trash'}
                  iconStyle={'far'}
                  color={'red'}
                  customStyle={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '14px',
                    height: '14px',
                    padding: 0,
                    margin: 0,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                />
              </div>
            );
          }}>
          <div css={styles.draggerContainer}>
            <p className="ant-upload-drag-icon" style={{ width: '100%' }}>
              <InboxOutlined />
            </p>
            <p className="ant-upload-text" style={{ width: '100%' }}>
              {intl.formatMessage({
                id: 'click-or-drag-file-here',
              })}
            </p>
            <p className="ant-upload-hint" style={{ width: '100%' }}>
              {intl.formatMessage({
                id: 'support-bulk-upload',
              })}
            </p>
          </div>
        </Dragger>
      </div>
    </Modal>
  );
};
